/**
 * recruitmentFields.js
 *
 * Field schema for the JBD Talent recruitment forms (recruit.jbdgobs.com),
 * covering all 13 positions. Every form shares a common core (consent,
 * contact, personal, education) and a common tail (compensation, availability,
 * emergency); the middle is position-specific.
 *
 * Used to render an imported application's raw answers (stored on the
 * Recruitment record's `externalData.data`) grouped by section with proper
 * human labels — mirroring how the form is laid out on the portal.
 */

// Portal role slug -> display title (all 13 positions)
export const POSITION_TITLES = {
  actor: 'Actor',
  editor: 'Editor',
  director: 'Director / Script Writer',
  cinematographer: 'Cinematographer / Camera',
  production_manager: 'Production Manager',
  sales_marketing: 'Sales & Marketing Executive',
  ai_creator: 'AI Content Creator',
  ai_automation: 'AI Automation & Prompt Specialist',
  content_head: 'Content Head / Programming Manager',
  series_producer: 'Series Producer / Showrunner',
  ott_product_manager: 'OTT Product Manager',
  content_operations: 'Content Operations Manager',
  adops_monetization: 'Ad Operations & Monetization Manager',
};

// field key -> human label (union across all positions)
export const FIELD_LABELS = {
  // Before you apply
  work_consent: 'Relocation consent (Karur office)',
  // Contact
  name: 'Name', email: 'Email', contact: 'Contact & WhatsApp number',
  // Personal
  gender: 'Gender', date_of_birth: 'Date of birth', age: 'Age',
  native_location: 'Native place', current_location: 'Current location',
  current_location_other: 'Current location (other)', marriage: 'Marital status',
  // Education
  education: 'Education', current_status: 'What are you doing now?',
  // Casting (actor)
  height_cm: 'Height (cm)', build: 'Build', languages: 'Languages spoken',
  tamil_fluency: 'Tamil fluency', comfortable_roles: 'Comfortable role types',
  acting_experience: 'Acting experience?', acting_training: 'Acting training / workshops',
  acting_video_link: 'Acting video link', acting_video_links: 'Acting video links',
  // Editor
  prior_experience: 'Prior editing experience?', where_worked: 'Where did you work?',
  editing_years: 'Years of editing experience', premiere_years: 'Adobe Premiere Pro (years)',
  aftereffects_years: 'Adobe After Effects (years)', photoshop_years: 'Adobe Photoshop (years)',
  other_software: 'Other editing software', other_software_text: 'Other software (specify)',
  own_editing_system: 'Owns editing PC / laptop?', system_specs: 'System specs',
  turnaround: 'Average turnaround per video', colour_grading_level: 'Colour grading proficiency',
  motion_graphics_level: 'Motion graphics / VFX level', content_length_pref: 'Short-form or long-form?',
  subtitling: 'Subtitling / captioning?', thumbnail_design: 'Can design thumbnails?',
  sample_edit_test: 'Willing to do a sample edit test?', film_link: 'Showreel / sample link',
  // Director
  direction_experience: 'Prior direction experience?', direction_where: 'Direction — where?',
  direction_years: 'Years of direction experience', content_writing_experience: 'Prior content writing experience?',
  content_writing_where: 'Content writing — where?', content_writing_years: 'Years of content writing',
  software_used: 'Software used', other_media_skills: 'Other media skills',
  other_skills_text: 'Other skills (specify)', director_genres: 'Genres you write / direct',
  writing_languages: 'Languages you write in', projects_count: 'Scripts written / projects directed',
  format_experience: 'Formats worked on', own_ideas: 'Own ideas / scripts to pitch?',
  storyboarding: 'Storyboarding ability?', team_management: 'Crew / team management?',
  film_writing_link: 'Writing sample / showreel', produced_work_link: 'Produced / published work',
  // Cinematographer
  camera_experience: 'Prior camera / cinematography experience?', experience_years: 'Years of experience',
  cameras_text: 'Cameras used', cameras_choice: 'Cameras used (list)',
  owns_equipment: 'Equipment owned', lighting_level: 'Lighting setup experience',
  camera_operations: 'Can operate', colour_grading_knowledge: 'Colour grading knowledge?',
  cine_genres: 'Genres shot', own_vehicle_gear: 'Vehicle to transport gear?',
  long_shoot_days: 'Comfortable with long shoot days / handheld?',
  // Production Manager
  past_productions: 'Productions managed', skills: 'Key skills', experience_details: 'Experience details',
  max_budget: "Largest budget managed", max_team_size: 'Largest team / crew size',
  vendor_network: 'Vendor / location / equipment network?', permits_experience: 'Shoot permits experience?',
  pm_tools: 'Tools used', accounts_experience: 'Petty cash / accounts experience?',
  own_transport_licence: 'Own transport + driving licence?', coordination_languages: 'Coordination languages',
  // Sales & Marketing
  prior_sales: 'Prior sales / marketing experience?', sales_years: 'Years in sales / marketing',
  sales_domains: 'Sold / marketed', agency_network: 'Agency / brand contacts?',
  pitch_languages: 'Languages you can pitch in', target_comfort: 'Comfortable with sales targets?',
  crm_tools: 'CRM / tools used', has_vehicle: 'Own transport for client meetings?',
  portfolio_link: 'Portfolio / pitch deck / LinkedIn',
  // AI Content Creator
  ai_tools: 'AI tools used', ai_content_types: 'AI content created', ai_years: 'Years with AI content tools',
  ai_skill: 'Overall AI skill level', prompt_writing: 'Prompt writing skill', can_edit: 'Can edit / finish AI output?',
  showreel: 'AI work / showreel link',
  // AI Automation
  ai_platforms: 'AI platforms / models used', automation_tools: 'Automation tools used',
  can_code: 'Can code (Python / JS)?', built_examples: 'AI workflows / tools built', work_link: 'GitHub / portfolio / demo',
  // Content Head
  prior_programming: 'Content programming / commissioning experience?', programming_years: 'Years in content / programming',
  content_types_handled: 'Content programmed', ott_experience: 'Worked on an OTT / streaming platform?',
  content_languages: 'Content languages', team_led: 'Largest content team led',
  // Series Producer
  prior_producing: 'Long-form producing experience?', producing_years: 'Years producing',
  formats_produced: 'Formats produced', episodes_count: 'Episodes / projects produced', budget_managed: 'Largest production budget',
  // OTT Product Manager
  prior_pm: 'Product management experience?', pm_years: 'Years in product management',
  product_types: 'Products managed', ott_streaming_exp: 'Worked on an OTT / streaming product?',
  tech_comfort: 'Comfort with tech / dev teams', analytics_tools: 'Analytics tools used',
  // Content Operations
  prior_ops: 'Content operations / ingest experience?', ops_years: 'Years in content operations',
  ops_tasks: 'Tasks handled', cms_tools: 'CMS / MAM tools', volume_handled: 'Content volume handled',
  // Ad Ops & Monetization
  prior_adops: 'Ad operations / monetization experience?', adops_years: 'Years in ad ops / monetization',
  ad_platforms: 'Ad platforms used', monetization_models: 'Monetization models', revenue_managed: 'Ad revenue / scale managed',
  // Compensation / availability / emergency (tail)
  expected_salary: 'Expected salary (per month)', expected_joining_date: 'Expected date of joining',
  notice_period: 'Notice period', willing_outstation: 'Willing to travel outstation?',
  emergency_name: 'Emergency contact name', emergency_phone: 'Emergency contact number',
  heard_from: 'How did you hear about us?',
};

const CORE = [
  { title: 'Before You Apply', fields: ['work_consent'] },
  { title: 'Contact Details', fields: ['name', 'email', 'contact'] },
  { title: 'Personal Details', fields: ['gender', 'date_of_birth', 'age', 'native_location', 'current_location', 'current_location_other', 'marriage'] },
  { title: 'Education & Current Status', fields: ['education', 'current_status'] },
];

const TAIL = [
  { title: 'Expected Compensation', fields: ['expected_salary'] },
  { title: 'Availability', fields: ['expected_joining_date', 'notice_period', 'willing_outstation'] },
  { title: 'Emergency Contact & Referral', fields: ['emergency_name', 'emergency_phone', 'heard_from'] },
];

// Position-specific middle sections (files like photos/resume are omitted —
// photos aren't retained and resume is shown from the record's own column).
const SPECIFIC = {
  actor: [
    { title: 'Casting Details', fields: ['height_cm', 'build', 'languages', 'tamil_fluency', 'comfortable_roles'] },
    { title: 'Experience', fields: ['acting_experience', 'acting_training'] },
    { title: 'Portfolio', fields: ['acting_video_link', 'acting_video_links'] },
  ],
  editor: [
    { title: 'Experience', fields: ['prior_experience', 'where_worked', 'editing_years', 'premiere_years', 'aftereffects_years', 'photoshop_years', 'other_software', 'other_software_text', 'own_editing_system', 'system_specs', 'turnaround', 'colour_grading_level', 'motion_graphics_level', 'content_length_pref', 'subtitling', 'thumbnail_design', 'sample_edit_test'] },
    { title: 'Portfolio', fields: ['film_link'] },
  ],
  director: [
    { title: 'Experience', fields: ['direction_experience', 'direction_where', 'direction_years', 'content_writing_experience', 'content_writing_where', 'content_writing_years', 'software_used', 'other_media_skills', 'other_skills_text', 'director_genres', 'writing_languages', 'projects_count', 'format_experience', 'own_ideas', 'storyboarding', 'team_management'] },
    { title: 'Portfolio', fields: ['film_writing_link', 'produced_work_link'] },
  ],
  cinematographer: [
    { title: 'Experience', fields: ['camera_experience', 'where_worked', 'experience_years', 'cameras_text', 'cameras_choice', 'other_media_skills', 'other_skills_text', 'owns_equipment', 'lighting_level', 'camera_operations', 'colour_grading_knowledge', 'cine_genres', 'own_vehicle_gear', 'long_shoot_days'] },
    { title: 'Portfolio', fields: ['film_link'] },
  ],
  production_manager: [
    { title: 'Experience', fields: ['past_productions', 'skills', 'experience_details', 'max_budget', 'max_team_size', 'vendor_network', 'permits_experience', 'pm_tools', 'accounts_experience', 'own_transport_licence', 'coordination_languages'] },
  ],
  sales_marketing: [
    { title: 'Experience', fields: ['prior_sales', 'sales_years', 'sales_domains', 'agency_network', 'pitch_languages', 'target_comfort', 'crm_tools', 'has_vehicle'] },
    { title: 'Portfolio', fields: ['portfolio_link'] },
  ],
  ai_creator: [
    { title: 'Experience', fields: ['ai_tools', 'ai_content_types', 'ai_years', 'ai_skill', 'prompt_writing', 'can_edit'] },
    { title: 'Portfolio', fields: ['showreel'] },
  ],
  ai_automation: [
    { title: 'Experience', fields: ['ai_platforms', 'automation_tools', 'can_code', 'ai_years', 'ai_skill', 'built_examples'] },
    { title: 'Portfolio', fields: ['work_link'] },
  ],
  content_head: [
    { title: 'Experience', fields: ['prior_programming', 'programming_years', 'content_types_handled', 'ott_experience', 'content_languages', 'team_led'] },
    { title: 'Portfolio', fields: ['portfolio_link'] },
  ],
  series_producer: [
    { title: 'Experience', fields: ['prior_producing', 'producing_years', 'formats_produced', 'episodes_count', 'budget_managed', 'own_ideas'] },
    { title: 'Portfolio', fields: ['work_link'] },
  ],
  ott_product_manager: [
    { title: 'Experience', fields: ['prior_pm', 'pm_years', 'product_types', 'ott_streaming_exp', 'tech_comfort', 'analytics_tools'] },
    { title: 'Portfolio', fields: ['work_link'] },
  ],
  content_operations: [
    { title: 'Experience', fields: ['prior_ops', 'ops_years', 'ops_tasks', 'cms_tools', 'volume_handled', 'ott_experience'] },
  ],
  adops_monetization: [
    { title: 'Experience', fields: ['prior_adops', 'adops_years', 'ad_platforms', 'monetization_models', 'revenue_managed', 'analytics_tools'] },
  ],
};

/** Ordered sections (core + position-specific + tail) for a role slug. */
export function sectionsForPosition(slug) {
  const key = String(slug || '').toLowerCase();
  return [...CORE, ...(SPECIFIC[key] || []), ...TAIL];
}

/** Derive the role slug from a Recruitment record (externalData.role or title). */
export function slugFromRecord(rec) {
  const raw = rec?.externalData?.role || rec?.externalData?.data?.role;
  if (raw && POSITION_TITLES[String(raw).toLowerCase()]) return String(raw).toLowerCase();
  const title = (rec?.position || '').toLowerCase();
  const hit = Object.entries(POSITION_TITLES).find(([, t]) => t.toLowerCase() === title);
  return hit ? hit[0] : null;
}

export const humanLabel = (key) => FIELD_LABELS[key] || key.replace(/_/g, ' ');
